import React, { useState } from 'react';
import { useUnifiedProfileData } from '@/hooks/profile/use-unified-profile-data';
import { useProfilePhoto } from '@/hooks/profile/use-profile-photo';
import { getInitials, formatJoinDate } from './utils';
import ProfileAvatar from './ProfileAvatar';
import ProfileInfo from './ProfileInfo';
import FlatDashboardCard from '@/components/ui/FlatDashboardCard';
import ProfileSidebarSkeleton from './ProfileSidebarSkeleton';
import TabbedImageUploadModal from '@/components/ui/upload/TabbedImageUploadModal';

const ProfileSidebar = () => {
  const { profileData, isLoading } = useUnifiedProfileData();
  const { uploadProfilePhoto, removeProfilePhoto, uploadStatus } = useProfilePhoto();
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const handlePhotoUpload = async (file: File): Promise<string> => {
    return await uploadProfilePhoto(file);
  };

  const handleRemovePhoto = async () => {
    await removeProfilePhoto();
  };

  if (isLoading) {
    return <ProfileSidebarSkeleton />;
  }

  const fullName = `${profileData.firstName || ''} ${profileData.lastName || ''}`.trim() || 'User';
  const initials = getInitials(profileData.firstName || '', profileData.lastName || '');
  const formattedJoinDate = formatJoinDate(profileData.createdAt || '');
  const userRole = profileData.userRole || 'User';
  
  return (
    <FlatDashboardCard>
      <div className="flex flex-col items-center p-6">
        {/* Profile Avatar Section */}
        <div className="mb-8">
          <ProfileAvatar
            profilePictureUrl={profileData.profilePictureUrl}
            initials={initials}
            onUploadClick={() => setIsUploadModalOpen(true)}
            onRemoveClick={profileData.profilePictureUrl ? handleRemovePhoto : undefined}
          />
        </div>
        
        {/* Profile Info Section */}
        <div className="w-full">
          <ProfileInfo
            fullName={fullName}
            userRole={userRole}
            joinDate={formattedJoinDate}
            email={profileData.email}
          />
        </div>

        {/* Tabbed Image Upload Modal */}
        <TabbedImageUploadModal
          isOpen={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
          autoUpload={true}
          showProgress={true}
          onUpload={handlePhotoUpload}
          title="Add Profile Photo"
          description="Upload or generate a profile photo"
          maxSizeMB={10} // 10MB - WebP conversion handles optimization
          allowedTypes={['image/jpeg', 'image/png', 'image/webp']}
          aspectRatio={1}
          previewSize="md"
          accentColor="purple"
        />
      </div>
    </FlatDashboardCard>
  );
};

export default ProfileSidebar;
